import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';
import domtoimage from 'dom-to-image';
import JSZip from 'jszip';
import React from 'react';

/**
 * Converts a data URL to a base64 string without the data URL prefix
 */
const dataUrlToBase64 = (dataUrl: string): string => {
  const base64Index = dataUrl.indexOf(',');
  return dataUrl.substring(base64Index + 1);
};

/**
 * Generates a unique filename for the shared image
 */
const generateImageFilename = (shipName: string): string => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19); // Format: YYYY-MM-DDTHH-MM-SS
  const shipNameSlug = shipName.replace(/\s+/g, '-').toLowerCase();
  return `cruise-countdown-${shipNameSlug}-${timestamp}.png`;
};

/**
 * Shares an image using native sharing capabilities on mobile devices
 * or falls back to download on web
 */
export const shareCountdownImage = async (
  elementRef: React.RefObject<HTMLDivElement>,
  cruiseInfo: {
    shipName: string;
    cruiseLine: string;
    daysUntilDeparture: number;
    format?: 'feed' | 'stories';
    itineraryTitle?: string | null;
  }
): Promise<void> => {
  if (!elementRef.current) {
    throw new Error('Element reference is not available');
  }

  try {
    // Add a small delay to ensure all styles are properly applied
    await new Promise(resolve => setTimeout(resolve, 100));

    // Define dimensions based on format
    const format = cruiseInfo.format || 'feed';
    const dimensions = format === 'stories' 
      ? { width: 1080, height: 1920 } 
      : { width: 1200, height: 630 };

    // Generate the image using dom-to-image
    const dataUrl = await domtoimage.toPng(elementRef.current, {
      quality: 1.0,
      bgcolor: undefined,
      width: dimensions.width,
      height: dimensions.height,
      style: {
        'transform': 'scale(1)',
        'transform-origin': 'top left',
        'width': `${dimensions.width}px`,
        'height': `${dimensions.height}px`,
        'margin': '0',
        'padding': '0',
        'position': 'relative',
        'top': '0',
        'left': '0'
      }
    });

    if (Capacitor.isNativePlatform()) {
      // Native platform: use Capacitor Share plugin
      await shareImageNative(dataUrl, cruiseInfo);
    } else {
      // Web platform: download the image
      await downloadImage(dataUrl, cruiseInfo);
    }
  } catch (error) {
    console.error('Error sharing countdown image:', error);
    throw new Error('Failed to generate or share the countdown image');
  }
};

/**
 * Shares image using native share sheet (Capacitor)
 */
const shareImageNative = async (
  dataUrl: string,
  cruiseInfo: {
    shipName: string;
    cruiseLine: string;
    daysUntilDeparture: number;
    itineraryTitle?: string | null;
  }
): Promise<void> => {
  try {
    const filename = generateImageFilename(cruiseInfo.shipName);
    const base64Data = dataUrlToBase64(dataUrl);

    console.log('Saving image file:', filename);
    console.log('Base64 data length:', base64Data.length);
    console.log('Base64 data starts with:', base64Data.substring(0, 50));

    // Try saving to Documents directory first (more reliable for sharing)
    let result;
    try {
      result = await Filesystem.writeFile({
        path: filename,
        data: base64Data,
        directory: Directory.Documents
      });
      console.log('File saved to Documents:', result.uri);
    } catch (docError) {
      console.warn('Failed to save to Documents, trying Cache:', docError);
      // Fallback to Cache directory
      result = await Filesystem.writeFile({
        path: filename,
        data: base64Data,
        directory: Directory.Cache
      });
      console.log('File saved to Cache:', result.uri);
    }

    // Verify the file was created correctly
    try {
      const fileInfo = await Filesystem.stat({
        path: filename,
        directory: Directory.Documents
      });
      console.log('File info:', fileInfo);
    } catch {
      try {
        const fileInfo = await Filesystem.stat({
          path: filename,
          directory: Directory.Cache
        });
        console.log('File info (Cache):', fileInfo);
      } catch (cacheStatError) {
        console.warn('Could not verify file creation:', cacheStatError);
      }
    }

    // Share the image using the native share sheet
    try {
      await Share.share({
        title: `${cruiseInfo.daysUntilDeparture} days until my cruise!`,
        text: cruiseInfo.itineraryTitle
          ? `Only ${cruiseInfo.daysUntilDeparture} days until I sail on the ${cruiseInfo.shipName} (${cruiseInfo.itineraryTitle}) with ${cruiseInfo.cruiseLine}! 🚢`
          : `Only ${cruiseInfo.daysUntilDeparture} days until I sail on the ${cruiseInfo.shipName} with ${cruiseInfo.cruiseLine}! 🚢`,
        url: result.uri,
        dialogTitle: 'Share your cruise countdown'
      });
    } catch (shareError) {
      // Check if it's a user cancellation
      const errorMessage = shareError instanceof Error ? shareError.message.toLowerCase() : '';
      if (errorMessage.includes('cancelled') || errorMessage.includes('canceled') || errorMessage.includes('user')) {
        // User cancelled - this is not an error, just return silently
        return;
      }
      // Re-throw actual errors
      throw shareError;
    }

    // Clean up the temporary file after a delay to ensure sharing is complete
    setTimeout(async () => {
      try {
        await Filesystem.deleteFile({
          path: filename,
          directory: Directory.Documents
        });
        console.log('Temporary file cleaned up successfully (Documents)');
      } catch {
        try {
          await Filesystem.deleteFile({
            path: filename,
            directory: Directory.Cache
          });
          console.log('Temporary file cleaned up successfully (Cache)');
        } catch (cacheCleanupError) {
          console.warn('Failed to clean up temporary image file:', cacheCleanupError);
        }
      }
    }, 15000); // Increased to 15 seconds to ensure sharing is complete

  } catch (error) {
    console.error('Error sharing image natively:', error);
    throw new Error('Failed to share image using native share sheet');
  }
};

/**
 * Downloads image for web platforms
 */
const downloadImage = async (
  dataUrl: string,
  cruiseInfo: {
    shipName: string;
    cruiseLine: string;
    daysUntilDeparture: number;
  }
): Promise<void> => {
  try {
    const filename = generateImageFilename(cruiseInfo.shipName);
    const link = document.createElement('a');
    link.download = filename;
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Error downloading image:', error);
    throw new Error('Failed to download the countdown image');
  }
};

/**
 * Checks if native sharing is available
 */
export const isNativeSharingAvailable = (): boolean => {
  return Capacitor.isNativePlatform();
};

/**
 * Generates all applicable Rewind slides as images
 * Returns array of { dataUrl, filename } objects
 */
export const generateAllRewindSlides = async (
  slideRefs: Array<React.RefObject<HTMLDivElement>>,
  slideNames: string[],
  year: number
): Promise<Array<{ dataUrl: string; filename: string }>> => {
  const slides: Array<{ dataUrl: string; filename: string }> = [];
  const dimensions = { width: 1080, height: 1920 };

  for (let i = 0; i < slideRefs.length; i++) {
    const ref = slideRefs[i];
    const slideName = slideNames[i];
    
    if (!ref.current) {
      console.warn(`Slide ref ${slideName} is not available, skipping`);
      continue;
    }

    try {
      // Add delay to ensure styles are applied
      await new Promise(resolve => setTimeout(resolve, 100));

      const dataUrl = await domtoimage.toPng(ref.current, {
        quality: 1.0,
        bgcolor: undefined,
        width: dimensions.width,
        height: dimensions.height,
        style: {
          'transform': 'scale(1)',
          'transform-origin': 'top left',
          'width': `${dimensions.width}px`,
          'height': `${dimensions.height}px`,
          'margin': '0',
          'padding': '0',
          'position': 'relative',
          'top': '0',
          'left': '0'
        }
      });

      const slideNumber = String(i + 1).padStart(2, '0'); // Pad with leading zero for proper sorting (01, 02, etc.)
      const filename = `rewind-${year}-slide${slideNumber}-${slideName}.png`;
      slides.push({ dataUrl, filename });
    } catch (error) {
      console.error(`Error generating slide ${slideName}:`, error);
      // Continue with other slides even if one fails
    }
  }

  return slides;
};

/**
 * Converts data URL to blob
 */
const dataUrlToBlob = (dataUrl: string): Blob => {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};

/**
 * Downloads all Rewind slides as a ZIP file
 */
export const downloadRewindSlidesZip = async (
  slides: Array<{ dataUrl: string; filename: string }>,
  year: number
): Promise<void> => {
  try {
    const zip = new JSZip();
    
    // Add each slide to the ZIP
    slides.forEach((slide) => {
      const blob = dataUrlToBlob(slide.dataUrl);
      zip.file(slide.filename, blob);
    });

    // Generate ZIP file
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    
    // Create download link
    const url = URL.createObjectURL(zipBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `cruising-along-rewind-${year}-slides.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error creating ZIP file:', error);
    throw new Error('Failed to create ZIP file');
  }
};

/**
 * Generates a unique filename for the Rewind shared image
 */
const generateRewindImageFilename = (year: number): string => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  return `cruising-along-rewind-${year}-${timestamp}.png`;
};

/**
 * Shares a Rewind year-in-review image using native sharing capabilities on mobile devices
 * or falls back to download on web
 */
export const shareRewindImage = async (
  elementRef: React.RefObject<HTMLDivElement>,
  year: number
): Promise<void> => {
  if (!elementRef.current) {
    throw new Error('Element reference is not available');
  }

  try {
    // Add a small delay to ensure all styles are properly applied
    await new Promise(resolve => setTimeout(resolve, 100));

    // Story format dimensions
    const dimensions = { width: 1080, height: 1920 };

    // Generate the image using dom-to-image
    const dataUrl = await domtoimage.toPng(elementRef.current, {
      quality: 1.0,
      bgcolor: undefined,
      width: dimensions.width,
      height: dimensions.height,
      style: {
        'transform': 'scale(1)',
        'transform-origin': 'top left',
        'width': `${dimensions.width}px`,
        'height': `${dimensions.height}px`,
        'margin': '0',
        'padding': '0',
        'position': 'relative',
        'top': '0',
        'left': '0'
      }
    });

    if (Capacitor.isNativePlatform()) {
      // Native platform: use Capacitor Share plugin
      await shareRewindImageNative(dataUrl, year);
    } else {
      // Web platform: download the image
      await downloadRewindImage(dataUrl, year);
    }
  } catch (error) {
    console.error('Error sharing Rewind image:', error);
    throw new Error('Failed to generate or share the Rewind image');
  }
};

/**
 * Shares Rewind image using native share sheet (Capacitor)
 */
const shareRewindImageNative = async (
  dataUrl: string,
  year: number
): Promise<void> => {
  try {
    const filename = generateRewindImageFilename(year);
    const base64Data = dataUrlToBase64(dataUrl);

    // Try saving to Documents directory first (more reliable for sharing)
    let result;
    try {
      result = await Filesystem.writeFile({
        path: filename,
        data: base64Data,
        directory: Directory.Documents
      });
    } catch {
      // Fallback to Cache directory
      result = await Filesystem.writeFile({
        path: filename,
        data: base64Data,
        directory: Directory.Cache
      });
    }

    // Share the image using the native share sheet
    try {
      await Share.share({
        title: `My Cruising Along Rewind ${year}`,
        text: `Check out my cruise year in review for ${year}! 🚢`,
        url: result.uri,
        dialogTitle: 'Share your Cruising Along Rewind'
      });
    } catch (shareError) {
      // Check if it's a user cancellation
      const errorMessage = shareError instanceof Error ? shareError.message.toLowerCase() : '';
      if (errorMessage.includes('cancelled') || errorMessage.includes('canceled') || errorMessage.includes('user')) {
        return;
      }
      throw shareError;
    }

    // Clean up the temporary file after a delay
    setTimeout(async () => {
      try {
        await Filesystem.deleteFile({
          path: filename,
          directory: Directory.Documents
        });
      } catch {
        try {
          await Filesystem.deleteFile({
            path: filename,
            directory: Directory.Cache
          });
        } catch (cacheCleanupError) {
          console.warn('Failed to clean up temporary Rewind image file:', cacheCleanupError);
        }
      }
    }, 15000);
  } catch (error) {
    console.error('Error sharing Rewind image natively:', error);
    throw new Error('Failed to share Rewind image using native share sheet');
  }
};

/**
 * Downloads Rewind image for web platforms
 */
const downloadRewindImage = async (
  dataUrl: string,
  year: number
): Promise<void> => {
  try {
    const filename = generateRewindImageFilename(year);
    const link = document.createElement('a');
    link.download = filename;
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Error downloading Rewind image:', error);
    throw new Error('Failed to download the Rewind image');
  }
};